import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Info, Pencil, RotateCcw, Wifi, WifiOff } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Field, Select } from '@/components/ui/field';
import { Dialog } from '@/components/ui/dialog';
import { TableWrap, Table, THead, TH, TR, TD, TableEmpty } from '@/components/ui/table';
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/states';
import { apiMessage, pruneBody, useRequireScope } from '../helpers';
import type { Branch } from '../types';
import ConfirmDialog from './ConfirmDialog';

/**
 * C-08 — §19 guest Wi-Fi: the branch network plus the per-area overrides.
 *
 * The `/wifi` controller has had area routes since P7-06 and nothing called
 * them: the only writer was `BranchFormDialog`, through `/branches`, which can
 * only ever set the branch-wide network. So a VIP room or a work-café zone on
 * its own SSID was unreachable from the product.
 *
 * There is no `WifiConfig` model. Branch credentials live in the `Branch`
 * columns (plus `Branch.settings.wifi` for the encryption/hidden flags) and an
 * area override lives in `Area.policies.wifi` — see `modules/wifi/`. Writes
 * always go through `/wifi/...` so the merge into those JSON bags stays in one
 * place; the *listing* reads `Area.policies.wifi` straight off `GET /areas`,
 * because asking `/wifi/areas/:id` per row would be one request per area to
 * learn something the list already carries.
 */

const WIFI_ENCRYPTIONS = ['WPA', 'WEP', 'nopass'] as const;
type WifiEncryption = (typeof WIFI_ENCRYPTIONS)[number];

/** The projection every `/wifi` read answers with (`WifiConfigView`). */
interface WifiConfigView {
  scope: 'BRANCH' | 'AREA';
  branchId: string;
  areaId: string | null;
  isOverride: boolean;
  ssid: string | null;
  password: string | null;
  encryption: WifiEncryption;
  hidden: boolean;
  qrPayload: string | null;
}

/** What `Area.policies.wifi` stores when the area runs its own network. */
interface StoredWifi {
  ssid?: string | null;
  password?: string | null;
  encryption?: WifiEncryption;
  hidden?: boolean;
}

interface WifiArea {
  id: string;
  name: string;
  floorId: string;
  policies: unknown;
  floor?: { id: string; name: string; branchId: string } | null;
  _count?: { tables: number; resources: number };
}

interface WifiForm {
  ssid: string;
  password: string;
  encryption: WifiEncryption;
  hidden: boolean;
}

const EMPTY_FORM: WifiForm = { ssid: '', password: '', encryption: 'WPA', hidden: false };

function formOf(config: StoredWifi | WifiConfigView | null | undefined): WifiForm {
  if (!config) return EMPTY_FORM;
  return {
    ssid: config.ssid ?? '',
    password: config.password ?? '',
    encryption: config.encryption ?? 'WPA',
    hidden: config.hidden ?? false,
  };
}

/** The override an area carries, or null when it inherits the branch network. */
function overrideOf(area: WifiArea): StoredWifi | null {
  const policies = area.policies;
  if (!policies || typeof policies !== 'object' || Array.isArray(policies)) return null;
  const wifi = (policies as Record<string, unknown>).wifi;
  if (!wifi || typeof wifi !== 'object' || Array.isArray(wifi)) return null;
  const stored = wifi as StoredWifi;
  // The service treats a missing SSID as "no override" — mirror that exactly,
  // or a half-written policy bag would read as an override to nowhere.
  return stored.ssid ? stored : null;
}

/** An open network needs no password, and sending one would be a lie on the QR. */
function bodyOf(form: WifiForm) {
  return pruneBody({
    ssid: form.ssid.trim(),
    password: form.encryption === 'nopass' ? '' : form.password,
    encryption: form.encryption,
    hidden: form.hidden,
  });
}

/** The SSID / password / encryption / hidden quartet, shared by both editors. */
function WifiFields({
  value,
  onChange,
  disabled,
  idPrefix,
}: {
  value: WifiForm;
  onChange: (next: WifiForm) => void;
  disabled: boolean;
  idPrefix: string;
}) {
  const { t } = useTranslation();
  const open = value.encryption === 'nopass';

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label={t('settings.wifi.ssid')} required>
        <Input
          value={value.ssid}
          maxLength={32}
          disabled={disabled}
          dir="ltr"
          onChange={(e) => onChange({ ...value, ssid: e.target.value })}
        />
      </Field>

      <Field
        label={t('settings.wifi.password')}
        hint={open ? t('settings.wifi.openNetworkHint') : undefined}
      >
        <Input
          value={open ? '' : value.password}
          maxLength={63}
          disabled={disabled || open}
          dir="ltr"
          onChange={(e) => onChange({ ...value, password: e.target.value })}
        />
      </Field>

      <Field label={t('settings.wifi.encryption')}>
        <Select
          value={value.encryption}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, encryption: e.target.value as WifiEncryption })}
        >
          {WIFI_ENCRYPTIONS.map((mode) => (
            <option key={mode} value={mode}>
              {t(`settings.wifi.encryptionMode.${mode}`)}
            </option>
          ))}
        </Select>
      </Field>

      <Field label={t('settings.wifi.visibility')}>
        <label className="flex h-11 items-center gap-2 text-sm">
          <input
            id={`${idPrefix}-hidden`}
            type="checkbox"
            className="h-4 w-4"
            checked={value.hidden}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, hidden: e.target.checked })}
          />
          {t('settings.wifi.hidden')}
        </label>
      </Field>
    </div>
  );
}

export default function WifiPanel() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const inScope = useRequireScope();
  const canManage = can('settings.update');
  const canSeeAreas = can('floor_map.view');

  const branchesQuery = useApi<unknown>('/branches', { limit: 200 });
  const branches = rowsOf<Branch>(branchesQuery.data).rows;

  const [branchId, setBranchId] = useState('');
  const currentBranchId = branchId || branches[0]?.id || '';
  const branchName = branches.find((b) => b.id === currentBranchId)?.name ?? '';

  const branchQuery = useApi<WifiConfigView>(
    currentBranchId ? `/wifi/branches/${currentBranchId}` : null,
  );
  const areaParams = useMemo(() => ({ limit: 200, branchId: currentBranchId }), [currentBranchId]);
  const areasQuery = useApi<unknown>(
    currentBranchId && canSeeAreas ? '/areas' : null,
    areaParams,
  );
  const areas = rowsOf<WifiArea>(areasQuery.data).rows;

  const branchConfig = branchQuery.data;
  const [form, setForm] = useState<WifiForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);

  /**
   * Every `/wifi/branches/:branchId` route declares
   * `@RequireScope(BRANCH, 'branchId')` — the read included — so a branch-scoped
   * manager holding `settings.update` is still refused for someone else's
   * branch. The picker says which ones are theirs rather than letting the
   * selection turn into a 403.
   */
  const branchInScope = inScope('BRANCH', currentBranchId);
  const canManageBranch = canManage && branchInScope;

  /** `UpsertWifiConfigDto.ssid` is `@IsNotEmpty()`; blank is a 400, not a clear. */
  const branchSsidMissing = !form.ssid.trim();

  // The branch form is a draft of whatever the API last answered with.
  useEffect(() => setForm(formOf(branchConfig ?? null)), [branchConfig]);

  const [editingArea, setEditingArea] = useState<WifiArea | null>(null);
  const [resettingArea, setResettingArea] = useState<WifiArea | null>(null);

  const reloadAll = () => {
    branchQuery.reload();
    if (canSeeAreas) areasQuery.reload();
  };

  const saveBranch = async () => {
    if (!form.ssid.trim()) {
      toast.error(t('settings.wifi.ssidRequired'));
      return;
    }
    setSaving(true);
    try {
      await api.put(`/wifi/branches/${currentBranchId}`, bodyOf(form));
      toast.success(t('settings.wifi.branchSaved'));
      reloadAll();
    } catch (error) {
      toast.error(apiMessage(error, t('settings.errors.actionFailed')));
    } finally {
      setSaving(false);
    }
  };

  const clearBranch = async () => {
    try {
      await api.delete(`/wifi/branches/${currentBranchId}`);
      toast.success(t('settings.wifi.branchCleared'));
      reloadAll();
    } catch (error) {
      toast.error(apiMessage(error, t('settings.errors.actionFailed')));
    }
  };

  const resetArea = async () => {
    if (!resettingArea) return;
    try {
      await api.delete(`/wifi/areas/${resettingArea.id}`);
      toast.success(t('settings.wifi.areaReset'));
      reloadAll();
    } catch (error) {
      toast.error(apiMessage(error, t('settings.errors.actionFailed')));
    }
  };

  if (branchesQuery.loading) return <LoadingState label={t('common.loading')} />;
  if (branchesQuery.error) {
    return <ErrorState message={branchesQuery.error} onRetry={branchesQuery.reload} />;
  }
  if (!branches.length) {
    return <EmptyState title={t('settings.branches.empty')} hint={t('settings.wifi.needBranch')} />;
  }

  const branchHasNetwork = Boolean(branchConfig?.ssid);
  /** `PUT`/`DELETE /wifi/areas/:areaId` declare `@RequireScope(AREA, 'areaId')`. */
  const areaInScope = (area: WifiArea) => inScope('AREA', area.id);

  return (
    <div className="space-y-5">
      <div className="max-w-xs">
        <Field label={t('settings.wifi.branchLabel')}>
          <Select
            value={currentBranchId}
            onChange={(e) => setBranchId(e.target.value)}
            aria-label={t('settings.wifi.branchLabel')}
          >
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
                {inScope('BRANCH', branch.id) ? '' : ` — ${t('settings.wifi.branchOutOfScope')}`}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {/* §19 — the rule the whole screen exists to make visible. */}
      <div className="flex gap-3 rounded-lg bg-sky-50 dark:bg-sky-950/50 p-4 text-sm text-sky-900 dark:text-sky-200">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="space-y-1">
          <p className="font-medium">{t('settings.wifi.guestRuleTitle')}</p>
          <p>{t('settings.wifi.guestRule')}</p>
        </div>
      </div>

      <section className="space-y-4 rounded-xl border p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold">
              <Wifi className="h-4 w-4" />
              {t('settings.wifi.branchSection')}
            </h2>
            <p className="text-sm text-ink-muted">
              {t('settings.wifi.branchSectionHint')}
            </p>
          </div>
          {branchHasNetwork ? (
            <Badge tone="success">{t('settings.wifi.configured')}</Badge>
          ) : (
            <Badge tone="warning">{t('settings.wifi.notConfigured')}</Badge>
          )}
        </div>

        {branchQuery.loading ? (
          <LoadingState label={t('common.loading')} />
        ) : branchQuery.error ? (
          <ErrorState message={branchQuery.error} onRetry={branchQuery.reload} />
        ) : (
          <>
            <WifiFields
              value={form}
              onChange={setForm}
              disabled={!canManageBranch || saving}
              idPrefix="branch"
            />
            {canManage && !branchInScope && (
              <p className="text-sm text-amber-700 dark:text-amber-400">{t('settings.wifi.branchOutOfScope')}</p>
            )}
            {canManageBranch && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  onClick={() => void saveBranch()}
                  disabled={saving || branchSsidMissing}
                  title={branchSsidMissing ? t('settings.wifi.ssidRequired') : undefined}
                >
                  {saving ? t('settings.common.saving') : t('common.save')}
                </Button>
                {branchHasNetwork && (
                  <Button variant="outline" onClick={() => setClearing(true)} disabled={saving}>
                    <WifiOff className="h-4 w-4" />
                    {t('settings.wifi.clearBranch')}
                  </Button>
                )}
                {branchSsidMissing && (
                  <span className="text-xs text-ink-muted">
                    {t('settings.wifi.ssidRequired')}
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-base font-semibold">{t('settings.wifi.areasSection')}</h2>
          <p className="text-sm text-ink-muted">
            {t('settings.wifi.areasSectionHint')}
          </p>
        </div>

        {!canSeeAreas ? (
          <EmptyState title={t('settings.wifi.areasNoAccess')} />
        ) : areasQuery.loading ? (
          <LoadingState label={t('common.loading')} />
        ) : areasQuery.error ? (
          <ErrorState message={areasQuery.error} onRetry={areasQuery.reload} />
        ) : (
          <TableWrap>
            <Table>
              <THead>
                <tr>
                  <TH>{t('settings.wifi.area')}</TH>
                  <TH>{t('settings.wifi.floor')}</TH>
                  <TH>{t('settings.wifi.tables')}</TH>
                  <TH>{t('settings.wifi.guestsJoin')}</TH>
                  <TH>{t('settings.wifi.source')}</TH>
                  <TH className="text-end">{t('settings.common.actions')}</TH>
                </tr>
              </THead>
              <tbody>
                {!areas.length && (
                  <TableEmpty colSpan={6}>{t('settings.wifi.areasEmpty')}</TableEmpty>
                )}
                {areas.map((area) => {
                  const override = overrideOf(area);
                  // Exactly what `toAreaView()` resolves: the override when the
                  // area has one, the branch network otherwise.
                  const effective = override ?? branchConfig ?? null;
                  const tables = area._count?.tables ?? 0;
                  return (
                    <TR key={area.id}>
                      <TD className="font-medium">{area.name}</TD>
                      <TD className="text-ink-muted">{area.floor?.name ?? '—'}</TD>
                      <TD className="text-ink-muted">{tables}</TD>
                      <TD>
                        {effective?.ssid ? (
                          <span dir="ltr" className="font-mono text-xs">
                            {effective.ssid}
                          </span>
                        ) : (
                          <span className="text-xs text-amber-700 dark:text-amber-400">
                            {t('settings.wifi.noNetwork')}
                          </span>
                        )}
                      </TD>
                      <TD>
                        {override ? (
                          <Badge tone="brand">{t('settings.wifi.override')}</Badge>
                        ) : (
                          <Badge tone="neutral">{t('settings.wifi.inherited')}</Badge>
                        )}
                      </TD>
                      <TD className="text-end">
                        {canManage && (
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={!areaInScope(area)}
                              title={
                                areaInScope(area)
                                  ? undefined
                                  : t('settings.wifi.areaOutOfScope')
                              }
                              onClick={() => setEditingArea(area)}
                              aria-label={
                                override
                                  ? t('settings.wifi.editOverride')
                                  : t('settings.wifi.addOverride')
                              }
                            >
                              <Pencil className="h-4 w-4" />
                              {override
                                ? t('settings.wifi.editOverride')
                                : t('settings.wifi.addOverride')}
                            </Button>
                            {override && (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={!areaInScope(area)}
                                title={
                                  areaInScope(area)
                                    ? t('settings.wifi.resetArea')
                                    : t('settings.wifi.areaOutOfScope')
                                }
                                onClick={() => setResettingArea(area)}
                                aria-label={t('settings.wifi.resetArea')}
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        )}
                      </TD>
                    </TR>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
        )}

        <p className="text-xs text-ink-muted">
          {t('settings.wifi.unseatedNote')}
        </p>
      </section>

      <AreaOverrideDialog
        area={editingArea}
        branchName={branchName}
        branchSsid={branchConfig?.ssid ?? null}
        onClose={() => setEditingArea(null)}
        onSaved={reloadAll}
      />

      <ConfirmDialog
        open={clearing}
        title={t('settings.wifi.clearBranchTitle')}
        message={t('settings.wifi.clearBranchConfirm', { name: branchName })}
        confirmLabel={t('settings.wifi.clearBranch')}
        onConfirm={clearBranch}
        onClose={() => setClearing(false)}
      />

      <ConfirmDialog
        open={Boolean(resettingArea)}
        title={t('settings.wifi.resetAreaTitle')}
        message={t('settings.wifi.resetAreaConfirm', { name: resettingArea?.name ?? '' })}
        confirmLabel={t('settings.wifi.resetArea')}
        onConfirm={resetArea}
        onClose={() => setResettingArea(null)}
      />
    </div>
  );
}

/** §19 — "a VIP room or a gaming zone may run its own network". */
function AreaOverrideDialog({
  area,
  branchName,
  branchSsid,
  onClose,
  onSaved,
}: {
  area: WifiArea | null;
  branchName: string;
  branchSsid: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<WifiForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (area) setForm(formOf(overrideOf(area)));
  }, [area]);

  /** An override IS an SSID — `UpsertWifiConfigDto` refuses an empty one. */
  const ssidMissing = !form.ssid.trim();

  const save = async () => {
    if (!area || ssidMissing) return;
    setSaving(true);
    try {
      await api.put(`/wifi/areas/${area.id}`, bodyOf(form));
      toast.success(t('settings.wifi.areaSaved'));
      onSaved();
      onClose();
    } catch (error) {
      toast.error(apiMessage(error, t('settings.errors.actionFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={Boolean(area)}
      onClose={onClose}
      title={t('settings.wifi.overrideTitle', { name: area?.name ?? '' })}
      description={
        branchSsid
          ? t('settings.wifi.overrideDescription', { branch: branchName, ssid: branchSsid })
          : t('settings.wifi.overrideDescriptionNoBranch', { branch: branchName })
      }
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => void save()}
            disabled={saving || ssidMissing}
            title={ssidMissing ? t('settings.wifi.ssidRequired') : undefined}
          >
            {saving ? t('settings.common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <WifiFields value={form} onChange={setForm} disabled={saving} idPrefix="area" />
      {ssidMissing && (
        <p className="mt-3 text-xs text-ink-muted">
          {t('settings.wifi.ssidRequired')}
        </p>
      )}
    </Dialog>
  );
}
